import type { FC } from "react"
import { memo } from "react"
import { IconCheckOutline18 as Check } from "nucleo-ui-outline-18"
import { useToggle } from "react-use"

import type { IconType } from "@productlane/ui"
import { cn } from "@productlane/lib/src/cn"
import { Button, Popover, useBreakpoints } from "@productlane/ui"
import {
	Command,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@productlane/ui/src/components/internal/command"

import type * as PopoverPrimitive from "@radix-ui/react-popover"
import { useRole } from "@/lib/useRole"

interface SelectProps {
	onChange: (input: { id: string; name: string; color?: string }) => void
	item: {
		id: string | null
		name: string | null
	} | null
	Icon: IconType
	items: { name: string; id: string; color?: string }[]
	label: string
	side?: PopoverPrimitive.PopoverContentProps["side"]
	align?: PopoverPrimitive.PopoverContentProps["align"]
	modal?: PopoverPrimitive.PopoverProps["modal"]
}

// tooltip
// viewer role
export const Select: FC<SelectProps> = memo(
	({
		onChange,
		item,
		items,
		Icon,
		label,
		side = "left",
		align = "end",
		modal,
	}) => {
		const { role } = useRole()
		const [open, onOpenChange] = useToggle(false)
		const { isMobile } = useBreakpoints()

		// On mobile, open below instead of to the side to stay within viewport
		const responsiveSide = isMobile ? "bottom" : side

		return (
			<Popover
				open={open}
				onOpenChange={(open) => {
					if (role !== "ADMIN") return
					onOpenChange(open)
				}}
				modal={modal}
				className="p-0"
				side={responsiveSide}
				align={align}
				content={
					<Command>
						<CommandInput autoFocus placeholder={`Set ${label} ...`} />
						<CommandList>
							<CommandGroup>
								{items.map(({ name, id, color }) => {
									return (
										<CommandItem
											key={id}
											onSelect={() => {
												onChange({ id, name, color })
												onOpenChange(false)
											}}
										>
											<div className="flex items-center gap-2">
												<Icon color={color} className="mr-2 text-secondary" />
												<span className="flex-1 truncate">{name}</span>
												{item?.name === name && <Check className="h-4 w-4" />}
											</div>
										</CommandItem>
									)
								})}
							</CommandGroup>
						</CommandList>
					</Command>
				}
			>
				<div className="w-full">
					<Button
						variant="ghost"
						className={cn(
							"w-full max-w-none justify-start text-xs hover:bg-gray-150 dark:hover:bg-gray-800",
							{
								"!text-tertiary": !item?.name,
								"!text-primary": item?.name,
							},
						)}
						Icon={() => (
							<Icon
								className={cn(
									item?.name
										? "text-secondary dark:text-primary"
										: "text-tertiary",
								)}
							/>
						)}
					>
						{item?.name ?? `Set ${label}`}
					</Button>
				</div>
			</Popover>
		)
	},
)
